import type { ResponseOf } from '@tuyau/core/types'
import type { routes } from 'gozoki-backend/registry'
import { LABEL_COLLATOR, sortKiosksByLabel } from '../sort-kiosks'

// Imports relatifs uniquement (pas de `~`) : testable en `node --test` (`cart-control.test.mjs`).

/**
 * Règles pures du panneau « Contrôle du panier » de l'écran Caméras (ancien écran
 * `/carts-control`) : caisses de la boutique, caisse affichée, qui tient une caisse en mode
 * support, verrou de la prise de main, statuts et mémoire du poste.
 */

/**
 * Ce que le panneau lit d'une caisse de sortie (`GET /kiosks` : toutes celles du périmètre de la
 * consultation ou de la prise de main, projetées par le serveur).
 */
export type CartControlKioskRow = ResponseOf<(typeof routes)['kiosks.index']>[number]

/** Article du panier contrôlé (`GET /carts/:id`). */
export type CartControlArticle = ResponseOf<
  (typeof routes)['carts.retrieve_cart']
>['articles'][number]

/** Caisses d'une boutique, par libellé : « Caisse 2 » avant « Caisse 10 ». */
export const storeCartControlKiosks = (
  kiosks: readonly CartControlKioskRow[],
  storeId: number,
): CartControlKioskRow[] => sortKiosksByLabel(kiosks.filter((kiosk) => kiosk.storeId === storeId))

/**
 * Qui tient une caisse : personne (mode normal), ce poste, ou un autre poste. Le backend ne sait
 * pas quel admin a pris la main : ce poste retient les caisses qu'il a prises (`HeldKiosk`).
 */
export type KioskHolder = 'none' | 'this-post' | 'other-post'

export const kioskHolder = (
  kiosk: Pick<CartControlKioskRow, 'id' | 'supportMode'>,
  heldKioskIds: readonly number[],
): KioskHolder => {
  if (!kiosk.supportMode) return 'none'
  return heldKioskIds.includes(kiosk.id) ? 'this-post' : 'other-post'
}

/**
 * Caisse affichée : celle de l'URL quand elle est dans la boutique, sinon celle que ce poste tient
 * (on la retrouve en rouvrant le panneau), sinon la première qu'aucun autre poste ne tient ; `null`
 * sans caisse libre. Une caisse tenue ailleurs ne s'affiche que demandée.
 */
export const selectCartControlKiosk = (
  storeKiosks: readonly CartControlKioskRow[],
  kioskId: number | undefined,
  heldKioskIds: readonly number[],
): CartControlKioskRow | null =>
  storeKiosks.find((kiosk) => kiosk.id === kioskId) ??
  storeKiosks.find((kiosk) => kioskHolder(kiosk, heldKioskIds) === 'this-post') ??
  storeKiosks.find((kiosk) => kioskHolder(kiosk, heldKioskIds) !== 'other-post') ??
  null

/**
 * Les caisses (`GET /kiosks`) ne se lisent que lorsqu'elles servent : panneau demandé, ou caisse
 * que ce poste tient (bandeau de rappel, et sa mémoire à purger). Panneau fermé sans caisse tenue,
 * ni requête ni relecture par le temps réel.
 */
export const shouldReadCartControlKiosks = ({
  authorized,
  requested,
  heldCount,
}: {
  authorized: boolean
  requested: boolean
  heldCount: number
}): boolean => authorized && (requested || heldCount > 0)

export type CartControlPanelState = 'hidden' | 'loading' | 'error' | 'empty' | 'ready'

/**
 * État du panneau : fermé sans `controle` dans l'URL ou sans la garde ; en chargement tant que les
 * caisses arrivent, en erreur si elles n'arrivent pas ; vide si la boutique n'a aucune caisse que
 * le rôle peut contrôler (droit et périmètre, ceux de `GET /kiosks`).
 */
export const cartControlPanelState = ({
  requested,
  authorized,
  isPending,
  isError,
  storeKioskCount,
}: {
  requested: boolean
  authorized: boolean
  isPending: boolean
  isError: boolean
  storeKioskCount: number
}): CartControlPanelState => {
  if (!requested || !authorized) return 'hidden'
  if (isPending) return 'loading'
  if (isError) return 'error'
  return storeKioskCount > 0 ? 'ready' : 'empty'
}

/**
 * Tant que ce poste tient la caisse affichée, ni la boutique ni la caisse ne changent et le panneau
 * ne se ferme pas : on rend d'abord la main (« Valider le panier »). Une caisse tenue par un autre
 * poste ne fige rien : le panneau la montre en lecture seule.
 */
export const isKioskSwitchLocked = (
  kiosk: Pick<CartControlKioskRow, 'id' | 'supportMode'> | null,
  heldKioskIds: readonly number[],
): boolean => kiosk !== null && kioskHolder(kiosk, heldKioskIds) === 'this-post'

export type CartControlAvailability = 'hidden' | 'available' | 'locked'

/**
 * Bouton « Contrôler le panier » d'une caisse (widget et modale « Bornes de caisse ») : absent si
 * les caisses lues montrent que le rôle ne peut pas la contrôler d'ici, désactivé tant que le
 * panneau montre une autre caisse que ce poste tient (`isKioskSwitchLocked`). Caisses pas encore
 * lues (panneau fermé) : actif, le périmètre se vérifie à l'ouverture du panneau.
 */
export const cartControlAvailability = ({
  storeKiosks,
  lockedKioskId,
  kioskId,
}: {
  /** Caisses de la boutique lues, `undefined` tant qu'elles ne le sont pas. */
  storeKiosks: readonly Pick<CartControlKioskRow, 'id'>[] | undefined
  /** Caisse du panneau que ce poste tient, `null` sans verrou. */
  lockedKioskId: number | null
  kioskId: number
}): CartControlAvailability => {
  if (storeKiosks !== undefined && !storeKiosks.some((kiosk) => kiosk.id === kioskId)) {
    return 'hidden'
  }
  return lockedKioskId !== null && lockedKioskId !== kioskId ? 'locked' : 'available'
}

/**
 * Une caisse à la fois par poste : ce poste tient-il déjà une autre caisse ? La même règle garde
 * « Passer en mode support » et « Reprendre la main ».
 */
export const holdsAnotherKiosk = (heldKioskIds: readonly number[], kioskId: number): boolean =>
  heldKioskIds.some((heldId) => heldId !== kioskId)

/**
 * Confirmation de « Reprendre la main » : ce qui se passe vraiment. Le serveur ne connaît pas le
 * poste détenteur : la reprise ne touche ni la caisse ni l'autre poste, qui garde ses boutons.
 */
export const takeOverConfirmation = (label: string): string =>
  `${label} est en mode support depuis un autre poste, ou sans poste connu. En reprenant la main, ce poste pourra modifier et valider son panier. L'autre poste n'est pas prévenu et garde ses boutons : prévenez l'opérateur concerné. La session de la caisse continue, le panier du client ne change pas.`

/** Raison affichée sur ce qui reste figé pendant une prise de main. */
export const CART_CONTROL_LOCKED_REASON = "Validez d'abord le panier de la caisse en mode support"

/** Message du panneau sur une caisse tenue ailleurs, affichée en lecture seule. */
export const OTHER_POST_HOLDER_MESSAGE = 'Prise en main par un autre poste'

export type KioskBadgeTone = 'neutral' | 'this-post' | 'other-post' | 'alert'

/**
 * Pastille de statut d'une caisse : mode normal, ou mode support et qui le tient. `cartId` reste
 * posé une fois le panier validé, il ne dit pas qu'un client est en caisse.
 */
export const KIOSK_HOLDER_BADGE: Record<
  KioskHolder,
  { label: string; tone: KioskBadgeTone; title: string | undefined }
> = {
  none: { label: 'Mode normal', tone: 'neutral', title: undefined },
  'this-post': { label: 'Mode support', tone: 'this-post', title: 'Prise en main par ce poste' },
  'other-post': { label: 'Mode support', tone: 'other-post', title: OTHER_POST_HOLDER_MESSAGE },
}

/**
 * Caisse que ce poste a prise en main : de quoi la reconnaître (verrou, lecture seule ailleurs) et
 * la rappeler, panneau fermé, depuis n'importe quelle boutique de l'écran Caméras.
 */
export interface HeldKiosk {
  kioskId: number
  storeId: number
  label: string
  /** Instant de la prise de main (ms). */
  takenAt: number
}

/**
 * Une liste relue juste après la prise de main peut encore montrer la caisse en mode normal
 * (requête partie avant) : on n'oublie une caisse qu'au-delà de ce délai.
 */
export const HELD_KIOSK_GRACE_MS = 30_000

const isPositiveInteger = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value > 0

/** Mémoire du poste, relue telle qu'écrite ; toute entrée illisible est ignorée. */
export const parseHeldKiosks = (raw: unknown): HeldKiosk[] =>
  Array.isArray(raw)
    ? raw.flatMap((entry: unknown) => {
        if (typeof entry !== 'object' || entry === null) return []
        const { kioskId, storeId, label, takenAt } = entry as Record<string, unknown>
        return isPositiveInteger(kioskId) &&
          isPositiveInteger(storeId) &&
          typeof label === 'string' &&
          typeof takenAt === 'number' &&
          Number.isFinite(takenAt)
          ? [{ kioskId, storeId, label, takenAt }]
          : []
      })
    : []

/** Prise ou reprise de main : la caisse rejoint la mémoire du poste (une entrée par caisse). */
export const holdKiosk = (
  held: readonly HeldKiosk[],
  kiosk: Pick<CartControlKioskRow, 'id' | 'storeId' | 'label'>,
  takenAt: number,
): HeldKiosk[] => [
  ...held.filter((entry) => entry.kioskId !== kiosk.id),
  { kioskId: kiosk.id, storeId: kiosk.storeId, label: kiosk.label, takenAt },
]

/** Panier validé : la caisse quitte la mémoire du poste. */
export const releaseHeldKiosk = (held: readonly HeldKiosk[], kioskId: number): HeldKiosk[] =>
  held.filter((entry) => entry.kioskId !== kioskId)

/** La caisse est-elle lue en mode support ? `GET /kiosks` rend tout le périmètre du rôle. */
const readInSupportMode = (
  kiosks: readonly Pick<CartControlKioskRow, 'id' | 'supportMode'>[],
  kioskId: number,
): boolean => kiosks.some((kiosk) => kiosk.id === kioskId && kiosk.supportMode)

/**
 * Caisses que ce poste tient encore : celles que la lecture des caisses montre en mode support. Une
 * caisse lue en mode normal (rendue d'un autre onglet ou d'un autre poste, réponse perdue) ou
 * absente de la lecture (supprimée, sortie du périmètre) ne l'est plus. `kiosks` non lues : la
 * mémoire telle quelle.
 */
export const activeHeldKiosks = (
  held: readonly HeldKiosk[],
  kiosks: readonly Pick<CartControlKioskRow, 'id' | 'supportMode'>[] | undefined,
): HeldKiosk[] =>
  kiosks === undefined
    ? [...held]
    : held.filter((entry) => readInSupportMode(kiosks, entry.kioskId))

/**
 * Mémoire à réécrire après une lecture des caisses (`fetchedAt`, ms) : les caisses qu'elle ne
 * montre pas en mode support (rendues, supprimées, hors périmètre) plus de `HELD_KIOSK_GRACE_MS`
 * après leur prise de main sont oubliées — sinon leur seul souvenir garderait la lecture des
 * caisses active. `null` : rien à oublier.
 */
export const pruneHeldKiosks = (
  held: readonly HeldKiosk[],
  kiosks: readonly Pick<CartControlKioskRow, 'id' | 'supportMode'>[],
  fetchedAt: number,
): HeldKiosk[] | null => {
  const kept = held.filter(
    (entry) =>
      fetchedAt - entry.takenAt <= HELD_KIOSK_GRACE_MS || readInSupportMode(kiosks, entry.kioskId),
  )
  return kept.length === held.length ? null : kept
}

/**
 * Redirection de l'ancienne URL `/carts-control` vers le panneau de l'écran Caméras : la caisse
 * demandée (`?controle`), sinon celle que ce poste tient, avec la boutique de l'URL ou, à défaut,
 * celle que la mémoire du poste connaît. Une caisse sans boutique connue garde sa place : l'écran
 * rejoint sa boutique une fois les caisses lues (`requestedKioskStoreId`). Sans caisse, l'écran
 * Caméras sur la boutique demandée.
 */
export const cartsControlRedirectSearch = ({
  storeId,
  controle,
  held,
}: {
  storeId?: number
  controle?: number
  held: readonly HeldKiosk[]
}): { storeId?: number; controle?: number } => {
  const kioskId = controle ?? held[0]?.kioskId
  if (kioskId === undefined) return storeId === undefined ? {} : { storeId }
  const kioskStoreId = storeId ?? held.find((entry) => entry.kioskId === kioskId)?.storeId
  return kioskStoreId === undefined
    ? { controle: kioskId }
    : { storeId: kioskStoreId, controle: kioskId }
}

/**
 * Boutique à rejoindre pour montrer la caisse demandée : la sienne quand les caisses lues la
 * situent ailleurs que la boutique affichée ; `undefined` sinon (déjà là, inconnue, non lue).
 */
export const requestedKioskStoreId = (
  kiosks: readonly Pick<CartControlKioskRow, 'id' | 'storeId'>[] | undefined,
  controle: number | undefined,
  storeId: number | undefined,
): number | undefined => {
  if (controle === undefined) return undefined
  const kioskStoreId = kiosks?.find((kiosk) => kiosk.id === controle)?.storeId
  return kioskStoreId === storeId ? undefined : kioskStoreId
}

/** Boutiques des caisses que le rôle peut contrôler, par nom : le sélecteur du panneau seul. */
export const cartControlStores = (
  kiosks: readonly Pick<CartControlKioskRow, 'storeId' | 'storeLabel'>[],
): { value: number; label: string }[] => {
  const stores = new Map<number, string>()
  for (const kiosk of kiosks) stores.set(kiosk.storeId, kiosk.storeLabel)
  return [...stores]
    .map(([value, label]) => ({ value, label }))
    .sort((left, right) => LABEL_COLLATOR.compare(left.label, right.label))
}

/**
 * Boutique du panneau seul (`/carts-control`, rôle sans le flux caméra) : celle de l'URL, sinon
 * celle de la caisse demandée, sinon celle de la caisse que ce poste tient, sinon la première des
 * boutiques contrôlables ; `undefined` tant que rien ne la dit.
 */
export const standaloneCartControlStoreId = ({
  storeId,
  controle,
  held,
  kiosks,
}: {
  storeId?: number
  controle?: number
  held: readonly HeldKiosk[]
  kiosks: readonly Pick<CartControlKioskRow, 'id' | 'storeId' | 'storeLabel'>[] | undefined
}): number | undefined =>
  storeId ??
  kiosks?.find((kiosk) => kiosk.id === controle)?.storeId ??
  held[0]?.storeId ??
  (kiosks === undefined ? undefined : cartControlStores(kiosks)[0]?.value)
